import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { getAuthHeaders } from '../services/api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const StatCard = ({ label, value }) => (
  <div className="bg-white border border-gray-200 rounded px-4 py-3">
    <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
    <div className="text-2xl font-light text-gray-700">{value}</div>
  </div>
);

const NormalizeNames = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const runningRef = React.useRef(false);

  if (!user?.is_admin) {
    return (
      <Layout>
        <div className="p-8 text-center text-gray-500">Admin access required.</div>
      </Layout>
    );
  }

  const run = async (apply) => {
    if (runningRef.current) return;
    runningRef.current = true;
    setRunning(true);
    try {
      const url = apply
        ? `${API_URL}/admin/normalize-names`
        : `${API_URL}/admin/normalize-names/dry-run`;
      const res = await axios.post(url, {}, { headers: getAuthHeaders() });
      setResult(res.data);
      if (apply) {
        toast.success(`Normalized ${res.data.field_rewrites} fields across ${res.data.projects_changed} projects.`);
      } else {
        toast.success('Dry run complete — review the preview below.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Operation failed');
    } finally {
      setRunning(false);
      runningRef.current = false;
    }
  };

  const handleApply = async () => {
    if (!result) {
      toast.error('Run a Dry Run first so you can review the changes.');
      return;
    }
    if (result.projects_changed === 0) {
      toast.info('Nothing to apply — every project is already canonical.');
      return;
    }
    const ok = window.confirm(
      `Apply ${result.field_rewrites} rewrites across ${result.projects_changed} projects?\n\nThis updates the database in place.`
    );
    if (!ok) return;
    await run(true);
  };

  return (
    <Layout>
      <div className="p-4 lg:p-8 max-w-5xl">
        <div className="mb-6">
          <h2 className="text-lg text-[#5B8DB8] font-light">Setup</h2>
          <h1 className="text-3xl text-gray-700 font-light italic">Normalize Employee Names</h1>
        </div>

        <button
          onClick={() => navigate('/setup')}
          className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded transition-colors mb-6"
          data-testid="back-btn"
        >
          Back
        </button>

        <div className="bg-white rounded shadow p-6 mb-6">
          <p className="text-gray-700 leading-relaxed">
            Legacy project records store the same employee in several formats —{' '}
            <code className="bg-gray-100 px-1 rounded">Pinto, Kelita</code>,{' '}
            <code className="bg-gray-100 px-1 rounded">K.Pinto</code>,{' '}
            <code className="bg-gray-100 px-1 rounded">Kelita Pinto</code>. This tool scans every
            project and rewrites each name field (Estimator, Office Contact, Production Manager,
            Marketing Rep, Project Lead, and per-PM rows) to the canonical{' '}
            <code className="bg-gray-100 px-1 rounded">Last, First</code> format from the
            Users &amp; Employees directory.
          </p>
          <p className="text-gray-500 text-sm mt-3">
            Always run a <strong>Dry Run</strong> first so you can review the proposed changes
            before committing. Names that don&apos;t map to any employee in the directory will be
            listed under <em>Unmatched</em> and left untouched.
          </p>

          <div className="flex gap-3 mt-5">
            <button
              onClick={() => run(false)}
              disabled={running}
              className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-5 py-2 rounded text-sm font-medium transition-colors disabled:opacity-60"
              data-testid="dry-run-btn"
            >
              {running ? 'Working…' : 'Dry Run'}
            </button>
            <button
              onClick={handleApply}
              disabled={running || !result || result.projects_changed === 0}
              className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="apply-btn"
            >
              {running ? 'Working…' : 'Apply Changes'}
            </button>
          </div>
        </div>

        {result && (
          <div className="bg-white rounded shadow p-6 space-y-6">
            <div>
              <h3 className="text-lg text-gray-700 mb-3">
                {result.applied ? 'Applied' : 'Preview'} — Summary
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard label="Projects scanned" value={result.projects_scanned} />
                <StatCard label="Projects to change" value={result.projects_changed} />
                <StatCard label="Field rewrites" value={result.field_rewrites} />
                <StatCard label="Unmatched names" value={result.unmatched_total} />
              </div>
              {result.warning && (
                <div className="mt-3 text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 text-sm">
                  {result.warning}
                </div>
              )}
            </div>

            {result.samples && result.samples.length > 0 && (
              <div>
                <h3 className="text-lg text-gray-700 mb-3">Sample Rewrites (first 50)</h3>
                <div className="overflow-x-auto border border-gray-200 rounded">
                  <table className="w-full text-sm" data-testid="sample-rewrites-table">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-2 text-gray-600 font-normal">Project #</th>
                        <th className="text-left p-2 text-gray-600 font-normal">Field</th>
                        <th className="text-left p-2 text-gray-600 font-normal">Before</th>
                        <th className="text-left p-2 text-gray-600 font-normal">After</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.samples.map((s, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="p-2 text-gray-700 font-mono text-xs">{s.project_id}</td>
                          <td className="p-2 text-gray-600">{s.field}</td>
                          <td className="p-2 text-red-600 line-through">{s.before}</td>
                          <td className="p-2 text-green-700">{s.after}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {result.unmatched && result.unmatched.length > 0 && (
              <div>
                <h3 className="text-lg text-gray-700 mb-1">
                  Unmatched Names ({result.unmatched_total})
                </h3>
                <p className="text-gray-500 text-sm mb-3">
                  These name values don&apos;t map to any employee in the directory and were left
                  alone. Add the employee in Users &amp; Employees and re-run if you want them
                  normalised.
                </p>
                <div className="overflow-x-auto border border-gray-200 rounded">
                  <table className="w-full text-sm" data-testid="unmatched-table">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-2 text-gray-600 font-normal">Project #</th>
                        <th className="text-left p-2 text-gray-600 font-normal">Field</th>
                        <th className="text-left p-2 text-gray-600 font-normal">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.unmatched.map((u, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="p-2 text-gray-700 font-mono text-xs">{u.project_id}</td>
                          <td className="p-2 text-gray-600">{u.field}</td>
                          <td className="p-2 text-gray-700">{u.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default NormalizeNames;
